import React, { Fragment, useCallback } from 'react';
import { defineMessages, useIntl } from 'react-intl-next';
import { styled } from '@compiled/react';

import Button from '@atlaskit/button/standard-button';
import { ModalTransition } from '@atlaskit/modal-dialog';
import { useAnalyticsEvents } from '@atlaskit/analytics-next';
import MediaServicesScaleLargeIcon from '@atlaskit/icon/glyph/media-services/scale-large';

import {
	UnsplashPopupMenu,
	PAGE_TITLE_DEFAULT_UNSPLASH_QUERY_WORD as CUSTOM_SITES_DEFAULT_UNSPLASH_QUERY_WORD,
} from '@confluence/custom-sites-extensions';

import type { AddHeaderImageButtonProps } from '../pageCoverPictureTypes';
import { DEFAULT_QUERY_WORD } from '../pageCoverPictureConstants';

import { ImagePickerModal } from './ImagePickerModal';
import { getUnsplashPhotos } from './unsplashAPI';

const i18n = defineMessages({
	addHeaderImage: {
		id: 'page-cover-picture.add.header.image.message',
		description:
			'Message displayed on the button which allows the user to change the header image in a page.',
		defaultMessage: 'Add header image',
	},
	headerLabel: {
		id: 'page-cover-picture.add.header.image.message.floating-toolbar',
		description:
			'Message displayed on the button which allows the user to change the header image in a page in the floating toolbar.',
		defaultMessage: 'Header image',
	},
});

type hasNoCoverPictureProps = {
	userOffline?: boolean;
	isTitleHovered?: boolean;
	isModalOpenState: boolean;
};

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const HasNoCoverPictureWrapper = styled.div<hasNoCoverPictureProps>({
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-dynamic-styles -- Ignored via go/DSP-18766
	opacity: (props) => (!props.isTitleHovered && !props.isModalOpenState ? 0 : 1),
});

export const AddHeaderImageButton = ({
	userOffline,
	isTitleHovered,
	openModal,
	openMediaBrowser,
	isModalOpenState,
	closeModal,
	handleUnsplashUpload,
	setIsUploadingPicture,
	initialUnsplashInfoArray,
	setInitialUnsplashInfoArray,
	coverPictureState,
	floatingToolbarButtonStyle,
	isCustomSitesPageTitleFFOn,
	isExternalShare,
	contentId,
}: AddHeaderImageButtonProps) => {
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const { formatMessage } = useIntl();

	const isPageTitleComponent = true;

	const onAddHeaderImageButtonHovered = useCallback(() => {
		if (initialUnsplashInfoArray.length === 0) {
			const queryWord = isCustomSitesPageTitleFFOn
				? CUSTOM_SITES_DEFAULT_UNSPLASH_QUERY_WORD
				: DEFAULT_QUERY_WORD;
			if (isCustomSitesPageTitleFFOn) {
				void getUnsplashPhotos(queryWord, 1, isPageTitleComponent).then((results) => {
					setInitialUnsplashInfoArray(results);
				});
			} else {
				void getUnsplashPhotos(queryWord, 1).then((results) => {
					setInitialUnsplashInfoArray(results);
				});
			}
		}
	}, [
		initialUnsplashInfoArray,
		isCustomSitesPageTitleFFOn,
		isPageTitleComponent,
		setInitialUnsplashInfoArray,
	]);

	const onAddHeaderImageButtonClicked = () => {
		openModal();
		createAnalyticsEvent({
			type: 'sendUIEvent',
			data: {
				action: 'clicked',
				actionSubject: 'button',
				actionSubjectId: 'addHeaderImageButtonClicked',
				source: 'editorPageCoverPicture',
			},
		}).fire();
	};

	const handleFocus = useCallback(() => {
		if (!isModalOpenState) {
			void coverPictureState.setIsAddHeaderImageButtonFocused(true);
		}
	}, [isModalOpenState, coverPictureState]);

	const handleBlur = useCallback(() => {
		void coverPictureState.setIsAddHeaderImageButtonFocused(false);
	}, [coverPictureState]);

	return (
		<HasNoCoverPictureWrapper
			isModalOpenState={isModalOpenState}
			isTitleHovered={isTitleHovered}
			userOffline={userOffline}
			data-testid="add-header-image-button-wrapper"
			onFocus={handleFocus}
			onBlur={handleBlur}
		>
			{isCustomSitesPageTitleFFOn ? (
				<UnsplashPopupMenu
					imageSrc=""
					initialUnsplashInfoArray={initialUnsplashInfoArray as any}
					openMediaBrowser={openMediaBrowser}
					handleUnsplashUpload={handleUnsplashUpload}
					setIsUploadingPicture={setIsUploadingPicture}
					coverPictureState={coverPictureState}
					closePopup={closeModal}
					userOffline={userOffline}
					floatingToolbarButtonStyle={floatingToolbarButtonStyle}
					isModalOpenState={isModalOpenState}
					onAddHeaderImageButtonClicked={onAddHeaderImageButtonClicked}
					onAddHeaderImageButtonHovered={onAddHeaderImageButtonHovered}
					contentId={contentId}
					isExternalShare={isExternalShare}
				/>
			) : (
				<Fragment>
					<Button
						appearance={isCustomSitesPageTitleFFOn ? 'subtle' : undefined}
						// eslint-disable-next-line @atlaskit/ui-styling-standard/enforce-style-prop -- Ignored via go/DSP-18766
						style={floatingToolbarButtonStyle || {}}
						testId="add-header-image-button"
						spacing="compact"
						iconBefore={<MediaServicesScaleLargeIcon label="image icon" size="small" />}
						onClick={onAddHeaderImageButtonClicked}
						isDisabled={userOffline}
						onMouseEnter={onAddHeaderImageButtonHovered}
					>
						{/* eslint-disable-next-line @atlaskit/ui-styling-standard/no-classname-prop -- Ignored via go/DSP-18766 */}
						<span className="add-header-image-button">
							{isCustomSitesPageTitleFFOn
								? formatMessage(i18n.headerLabel)
								: formatMessage(i18n.addHeaderImage)}
						</span>
					</Button>
					<ModalTransition>
						{isModalOpenState && (
							<ImagePickerModal
								initialUnsplashInfoArray={initialUnsplashInfoArray}
								closeModal={closeModal}
								openMediaBrowser={openMediaBrowser}
								userOffline={userOffline}
								handleUnsplashUpload={handleUnsplashUpload}
								setIsUploadingPicture={setIsUploadingPicture}
								coverPictureState={coverPictureState}
							/>
						)}
					</ModalTransition>
				</Fragment>
			)}
		</HasNoCoverPictureWrapper>
	);
};
